import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, CheckSquare, Square, Calendar, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import {
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  swapTasksOrder,
  Task,
  TaskCreatePayload,
  GetTasksFilters,
} from '../lib/services/tasksService';
import TaskForm from '../components/TaskForm';
import TaskDescriptionModal from '../components/TaskDescriptionModal';
import { format, parseISO, isToday, isPast } from 'date-fns';
import { fr } from 'date-fns/locale';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const STORAGE_HIDE_COMPLETED = 'suivifinance-tasks-hide-completed';

function readHideCompletedDefault(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_HIDE_COMPLETED);
    if (v === null) return true;
    return v === 'true';
  } catch {
    return true;
  }
}

const Tasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [descriptionTask, setDescriptionTask] = useState<Task | null>(null);
  const [filterScope, setFilterScope] = useState<'all' | 'this_month' | 'upcoming'>('all');
  const [filterMonth, setFilterMonth] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [hideCompleted, setHideCompleted] = useState<boolean>(readHideCompletedDefault);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_HIDE_COMPLETED, hideCompleted ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [hideCompleted]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    const filters: GetTasksFilters = {};
    if (filterMonth !== null && filterYear !== null) {
      filters.month = filterMonth;
      filters.year = filterYear;
    } else {
      filters.scope = filterScope;
    }
    const { data, error: err } = await getTasks(filters);
    setLoading(false);
    if (err) {
      setError(err?.message ?? 'Erreur lors du chargement');
      setTasks([]);
      return;
    }
    setTasks(data ?? []);
  }, [filterScope, filterMonth, filterYear]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleCreate = async (payload: TaskCreatePayload & { completed?: boolean }) => {
    const res = await createTask({
      title: payload.title,
      description: payload.description ?? null,
      due_date: payload.due_date,
    });
    if (res.error) return { success: false, error: res.error?.message ?? 'Erreur' };
    await loadTasks();
    return { success: true };
  };

  const handleUpdate = async (payload: TaskCreatePayload & { completed?: boolean }) => {
    if (!editingTask) return { success: false, error: 'Tâche inconnue' };
    const res = await updateTask(editingTask.id, {
      title: payload.title,
      description: payload.description ?? null,
      due_date: payload.due_date,
      ...(payload.completed !== undefined && { completed: payload.completed }),
    });
    if (res.error) return { success: false, error: res.error?.message ?? 'Erreur' };
    setEditingTask(null);
    await loadTasks();
    return { success: true };
  };

  const handleSubmit = async (payload: TaskCreatePayload & { completed?: boolean }) => {
    return editingTask ? handleUpdate(payload) : handleCreate(payload);
  };

  const handleDelete = async (task: Task) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    const res = await deleteTask(task.id);
    if (res.error) {
      setError(res.error?.message ?? 'Erreur lors de la suppression');
      return;
    }
    if (editingTask?.id === task.id) setEditingTask(null);
    if (descriptionTask?.id === task.id) setDescriptionTask(null);
    await loadTasks();
  };

  const toggleCompleted = async (task: Task) => {
    const res = await updateTask(task.id, { completed: !task.completed });
    if (!res.error) await loadTasks();
  };

  /** Liste affichée : masque les terminées si option activée. */
  const visibleTasks = hideCompleted ? tasks.filter((t) => !t.completed) : tasks;
  const hiddenCompletedCount = hideCompleted ? tasks.filter((t) => t.completed).length : 0;

  const moveTask = async (task: Task, direction: 'up' | 'down') => {
    setError(null);
    const list = visibleTasks;
    const idx = list.findIndex((t) => t.id === task.id);
    if (idx < 0) return;
    const j = direction === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= list.length) return;
    const other = list[j];
    const res = await swapTasksOrder(task.id, other.id);
    if (res.error) {
      setError(res.error?.message ?? 'Impossible de réordonner');
      return;
    }
    await loadTasks();
  };

  const openCreate = () => { setEditingTask(null); setIsFormOpen(true); };
  const openEdit = (t: Task) => { setEditingTask(t); setIsFormOpen(true); };
  const closeForm = () => { setIsFormOpen(false); setEditingTask(null); };
  const openDescription = (t: Task) => setDescriptionTask(t);
  const closeDescription = () => setDescriptionTask(null);

  const handleSaveDescription = async (taskId: string, description: string | null) => {
    const res = await updateTask(taskId, { description });
    if (res.error) return { success: false, error: res.error?.message ?? 'Erreur' };
    await loadTasks();
    if (res.data) setDescriptionTask(res.data);
    return { success: true };
  };

  const getDueBadge = (dueDate: string) => {
    const d = parseISO(dueDate);
    if (isPast(d) && !isToday(d)) return { label: 'En retard', className: 'bg-red-500/20 text-red-400' };
    if (isToday(d)) return { label: 'Aujourd\'hui', className: 'bg-primary-500/20 text-primary-500' };
    return null;
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 1 + i);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Tâches</h1>
          <p className="text-dark-400 mt-1">Gérez vos tâches et échéances</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50"
        >
          <Plus className="w-5 h-5" />
          Nouvelle tâche
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-dark-400">Afficher :</span>
        <div className="flex rounded-lg border border-dark-700 overflow-hidden">
          {(['all', 'this_month', 'upcoming'] as const).map((scope) => (
            <button
              key={scope}
              type="button"
              onClick={() => { setFilterScope(scope); setFilterMonth(null); setFilterYear(null); }}
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50 ${
                filterMonth === null && filterScope === scope
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
              }`}
            >
              {scope === 'all' ? 'Toutes' : scope === 'this_month' ? 'Ce mois' : 'À venir'}
            </button>
          ))}
        </div>
        <span className="text-dark-500">|</span>
        <select
          value={filterMonth ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') { setFilterMonth(null); setFilterYear(null); return; }
            setFilterMonth(parseInt(v, 10));
            if (filterYear === null) setFilterYear(currentYear);
          }}
          className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
        >
          <option value="">Mois précis</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>{m}</option>
          ))}
        </select>
        {(filterMonth !== null || filterYear !== null) && (
          <select
            value={filterYear ?? currentYear}
            onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
            className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}
        <span className="text-dark-500">|</span>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500/50"
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.target.checked)}
          />
          <span className="text-sm text-dark-300">Masquer les tâches terminées</span>
        </label>
        {hideCompleted && hiddenCompletedCount > 0 && (
          <span className="text-xs text-dark-500">
            ({hiddenCompletedCount} masquée{hiddenCompletedCount > 1 ? 's' : ''})
          </span>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="rounded-xl border border-dark-700 bg-dark-800/50 p-12 text-center">
          <Calendar className="w-12 h-12 text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 mb-2">Aucune tâche pour cette période</p>
          <button
            type="button"
            onClick={openCreate}
            className="text-primary-500 font-medium hover:text-primary-400 focus:outline-none focus:underline"
          >
            Créer ma première tâche
          </button>
        </div>
      ) : visibleTasks.length === 0 ? (
        <div className="rounded-xl border border-dark-700 bg-dark-800/50 p-12 text-center">
          <Calendar className="w-12 h-12 text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 mb-2">
            {tasks.some((t) => t.completed) && hideCompleted
              ? 'Toutes les tâches visibles sont terminées (ou aucune tâche en cours)'
              : 'Aucune tâche pour cette période'}
          </p>
          {tasks.some((t) => t.completed) && hideCompleted && (
            <button
              type="button"
              onClick={() => setHideCompleted(false)}
              className="text-primary-500 font-medium hover:text-primary-400 focus:outline-none focus:underline text-sm"
            >
              Afficher les tâches terminées
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          <p className="text-xs text-dark-500 mb-1">
            Ordre d&apos;importance : utilisez les flèches pour déplacer une tâche (parmi les lignes affichées).
          </p>
          {visibleTasks.map((task, vIdx) => {
            const badge = getDueBadge(task.due_date);
            const canUp = vIdx > 0;
            const canDown = vIdx < visibleTasks.length - 1;
            return (
              <li
                key={task.id}
                className={`rounded-xl border bg-dark-800/80 p-4 border-dark-700 transition-colors ${
                  task.completed ? 'opacity-70' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-0.5 shrink-0 pt-0.5">
                    <button
                      type="button"
                      onClick={() => moveTask(task, 'up')}
                      disabled={!canUp}
                      className="p-1 rounded text-dark-400 hover:text-white hover:bg-dark-700 disabled:opacity-25 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      title="Monter (plus important)"
                      aria-label="Monter"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveTask(task, 'down')}
                      disabled={!canDown}
                      className="p-1 rounded text-dark-400 hover:text-white hover:bg-dark-700 disabled:opacity-25 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      title="Descendre (moins important)"
                      aria-label="Descendre"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCompleted(task)}
                    className="mt-0.5 text-dark-400 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 rounded"
                    aria-label={task.completed ? 'Marquer non terminée' : 'Marquer terminée'}
                  >
                    {task.completed ? (
                      <CheckSquare className="w-5 h-5 text-primary-500" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`font-medium text-white ${task.completed ? 'line-through text-dark-400' : ''}`}>
                        {task.title}
                      </span>
                      {badge && (
                        <span className={`text-xs px-2 py-0.5 rounded ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <button
                        type="button"
                        onClick={() => openDescription(task)}
                        className="text-sm text-dark-400 mt-1 line-clamp-2 text-left hover:text-dark-200 focus:outline-none focus:underline"
                        title="Ouvrir la description complète"
                      >
                        {task.description}
                      </button>
                    )}
                    <p className="text-xs text-dark-500 mt-2">
                      Échéance : {format(parseISO(task.due_date), 'd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openDescription(task)}
                      className="p-2 text-dark-400 hover:text-primary-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      aria-label="Voir la description"
                      title="Voir / modifier la description"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(task)}
                      className="p-2 text-dark-400 hover:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      aria-label="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(task)}
                      className="p-2 text-dark-400 hover:text-red-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <TaskForm
        isOpen={isFormOpen}
        onClose={closeForm}
        task={editingTask}
        onSubmit={handleSubmit}
        title={editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}
      />

      <TaskDescriptionModal
        task={descriptionTask}
        isOpen={!!descriptionTask}
        onClose={closeDescription}
        onSaveDescription={handleSaveDescription}
      />
    </div>
  );
};

export default Tasks;
